import React from 'react';
import {
    Container, Box, Typography, Paper, Chip, Button,
    Skeleton, Divider, Grid, Stack, Avatar,
} from '@mui/material';
import {
    ArrowBack,
    Inventory2Outlined as BoxIcon,
    LocalShippingOutlined as ShipIcon,
    CheckCircleOutline as DoneIcon,
    CancelOutlined as CancelIcon,
    HourglassTopOutlined as PendingIcon,
    InfoOutlined as InfoIcon,
    PlaceOutlined as PinIcon,
    ReceiptLongOutlined as ReceiptIcon,
    PaymentOutlined as PayIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import marketplaceService from '../services/marketplaceService';
import { useAuth } from '../context/AuthContext';

const STATUS_CONFIG = {
    pending:   { label: 'Pending',   color: 'warning',   Icon: PendingIcon,  description: 'Your order has been placed and is awaiting seller confirmation.' },
    confirmed: { label: 'Confirmed', color: 'info',      Icon: InfoIcon,     description: 'The seller has confirmed your order and is preparing it for shipment.' },
    shipped:   { label: 'Shipped',   color: 'secondary', Icon: ShipIcon,     description: 'Your order is on its way! Expect delivery soon.' },
    delivered: { label: 'Delivered', color: 'success',   Icon: DoneIcon,     description: 'Your order has been delivered successfully.' },
    cancelled: { label: 'Cancelled', color: 'error',     Icon: CancelIcon,   description: 'This order has been cancelled.' },
};

const TIMELINE = ['pending', 'confirmed', 'shipped', 'delivered'];

const StatusTimeline = ({ currentStatus }) => {
    const currentIdx = TIMELINE.indexOf(currentStatus);
    const isCancelled = currentStatus === 'cancelled';

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            {TIMELINE.map((step, idx) => {
                const cfg = STATUS_CONFIG[step];
                const { Icon } = cfg;
                const done = !isCancelled && idx <= currentIdx;
                const active = !isCancelled && idx === currentIdx;

                return (
                    <React.Fragment key={step}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                            <Avatar
                                sx={{
                                    width: 36, height: 36,
                                    bgcolor: done ? `${cfg.color}.main` : 'action.disabledBackground',
                                    transition: 'all 0.3s',
                                    boxShadow: active ? `0 0 0 3px rgba(25,118,210,0.2)` : 'none',
                                }}
                            >
                                <Icon sx={{ fontSize: 18, color: done ? 'white' : 'action.disabled' }} />
                            </Avatar>
                            <Typography variant="caption" color={done ? 'text.primary' : 'text.disabled'} fontWeight={done ? 600 : 400} sx={{ whiteSpace: 'nowrap' }}>
                                {cfg.label}
                            </Typography>
                        </Box>
                        {idx < TIMELINE.length - 1 && (
                            <Box sx={{
                                flex: 1, height: 2, mx: 0.5, mb: 2.5,
                                bgcolor: (!isCancelled && idx < currentIdx) ? 'primary.main' : 'action.disabledBackground',
                                transition: 'background-color 0.3s',
                            }} />
                        )}
                    </React.Fragment>
                );
            })}
        </Box>
    );
};

const InfoRow = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.25, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
        <Typography variant="body2" fontWeight={600} sx={{ textAlign: 'right', maxWidth: '60%' }}>{value}</Typography>
    </Box>
);

// Detect carrier from tracking number pattern and return name + tracking URL
const getCarrierInfo = (trackingNumber) => {
    if (!trackingNumber) return null;
    const tn = trackingNumber.trim().toUpperCase();
    if (/^1Z[A-Z0-9]{16}$/.test(tn))
        return { name: 'UPS', url: `https://www.ups.com/track?tracknum=${tn}` };
    if (/^(\d{22}|\d{26}|96\d{20}|\d{15}|\d{12})$/.test(tn))
        return { name: 'FedEx', url: `https://www.fedex.com/fedextrack/?trknbr=${tn}` };
    if (/^(9400|9205|9270|9361|420)/.test(tn) || /^[A-Z]{2}\d{9}US$/.test(tn))
        return { name: 'USPS', url: `https://tools.usps.com/go/TrackConfirmAction?tLabels=${tn}` };
    if (/^\d{10,11}$/.test(tn) || /^[0-9]{12}$/.test(tn))
        return { name: 'DHL', url: `https://www.dhl.com/en/express/tracking.html?AWB=${tn}` };
    return { name: null, url: `https://parcelsapp.com/en/tracking/${tn}` };
};

const PurchaseDetailPage = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();
    const { isAuthenticated, loading: authLoading } = useAuth();

    const { data: order, isLoading, isError, error } = useQuery({
        queryKey: ['order', orderId],
        queryFn: () => marketplaceService.getOrder(orderId),
        staleTime: 60 * 1000,
        enabled: isAuthenticated,
        retry: (failCount, err) => {
            const status = err?.response?.status;
            if (status === 403 || status === 404) return false;
            return failCount < 2;
        },
    });

    // Fetch component details (mpn, manufacturer) for each order item
    const componentIds = order?.items?.map((i) => i.component_id) || [];
    const { data: componentMap = {} } = useQuery({
        queryKey: ['orderComponents', componentIds],
        queryFn: async () => {
            const results = await Promise.all(
                componentIds.map((id) => marketplaceService.getComponent(id).catch(() => null))
            );
            return Object.fromEntries(
                results.filter(Boolean).map((c) => [c.id, c])
            );
        },
        enabled: componentIds.length > 0,
        staleTime: 5 * 60 * 1000,
    });

    const formatDate = (iso) =>
        new Date(iso).toLocaleString('en-US', {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: '2-digit', minute: '2-digit',
        });

    if (authLoading) return null;

    if (isLoading) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Skeleton variant="text" width="40%" height={40} sx={{ mb: 2 }} />
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 3, mb: 2 }} />
                <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 3 }} />
            </Container>
        );
    }

    if (isError) {
        const is403 = error?.message?.includes('403');
        return (
            <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                    {is403 ? 'Access Denied' : 'Purchase Not Found'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                    {is403
                        ? "You don't have permission to view this order."
                        : error?.message || 'This order could not be loaded.'}
                </Typography>
                <Button variant="contained" onClick={() => navigate('/orders')}>Back to My Purchases</Button>
            </Container>
        );
    }

    const cfg = STATUS_CONFIG[order?.status] || { label: order?.status, color: 'default', Icon: BoxIcon, description: '' };
    const shipping = order?.shipping || {};
    const items = order?.items || [];

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                <Button
                    startIcon={<ArrowBack />}
                    onClick={() => navigate('/orders')}
                    variant="outlined"
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                    My Purchases
                </Button>
                <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>
                    Purchase #{order?.order_id}
                </Typography>
                <Chip
                    icon={<cfg.Icon fontSize="small" />}
                    label={cfg.label}
                    color={cfg.color}
                    sx={{ fontWeight: 700 }}
                />
            </Box>

            <Grid container spacing={3}>
                {/* Status card */}
                <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>Purchase Status</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            {cfg.description}
                        </Typography>
                        <StatusTimeline currentStatus={order?.status} />
                    </Paper>
                </Grid>

                {/* Items */}
                <Grid item xs={12}>
                    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
                        <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <ReceiptIcon fontSize="small" color="action" />
                                <Typography variant="subtitle2" fontWeight={700}>Items Purchased</Typography>
                            </Stack>
                        </Box>
                        <Box>
                            {items.map((item, idx) => (
                                <Box
                                    key={item.id ?? idx}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 2, px: 3, py: 2,
                                        borderBottom: idx < items.length - 1 ? '1px solid' : 'none',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <Avatar
                                        variant="rounded"
                                        sx={{ width: 44, height: 44, bgcolor: 'primary.50', borderRadius: 1.5 }}
                                    >
                                        <BoxIcon color="primary" fontSize="small" />
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="body2" fontWeight={600}>
                                            {componentMap[item.component_id]?.mpn || item.name || item.part_number || item.mpn || `Part #${item.component_id}`}
                                        </Typography>
                                        {(componentMap[item.component_id]?.manufacturer || item.manufacturer) && (
                                            <Typography variant="caption" color="text.secondary">
                                                {componentMap[item.component_id]?.manufacturer || item.manufacturer}
                                            </Typography>
                                        )}
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            Qty: {item.quantity} × ${parseFloat(item.unit_price).toFixed(4)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" fontWeight={700}>
                                        ${parseFloat(item.subtotal).toFixed(2)}
                                    </Typography>
                                </Box>
                            ))}
                            {/* Total row */}
                            <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'flex-end', gap: 6, bgcolor: 'grey.50' }}>
                                <Typography variant="subtitle1" fontWeight={700}>Total</Typography>
                                <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                                    ${parseFloat(order?.total || 0).toFixed(2)}
                                </Typography>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Shipping address */}
                <Grid item xs={12} sm={6}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <PinIcon fontSize="small" color="action" />
                            <Typography variant="subtitle2" fontWeight={700}>Shipping Address</Typography>
                        </Stack>
                        <Divider sx={{ mb: 2 }} />
                        <Stack spacing={0.5}>
                            <Typography variant="body2" fontWeight={600}>
                                {shipping.first_name} {shipping.last_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">{shipping.address}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                {shipping.city}, {shipping.state} {shipping.zip_code}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">{shipping.country}</Typography>
                            {shipping.phone && (
                                <Typography variant="body2" color="text.secondary">{shipping.phone}</Typography>
                            )}
                        </Stack>
                    </Paper>
                </Grid>

                {/* Order details */}
                <Grid item xs={12} sm={6}>
                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                            <PayIcon fontSize="small" color="action" />
                            <Typography variant="subtitle2" fontWeight={700}>Purchase Info</Typography>
                        </Stack>
                        <Divider sx={{ mb: 1 }} />
                        <InfoRow label="Placed" value={formatDate(order?.created_at)} />
                        <InfoRow label="Updated" value={formatDate(order?.updated_at)} />
                        <InfoRow
                            label="Payment"
                            value={order?.payment_method?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        />
                        <InfoRow
                            label="Status"
                            value={<Chip label={cfg.label} color={cfg.color} size="small" sx={{ fontWeight: 600 }} />}
                        />
                        {/* Tracking — always show row when shipped/delivered */}
                        {(order?.tracking_number || ['shipped', 'delivered'].includes(order?.status)) && (() => {
                            if (!order?.tracking_number) {
                                return <InfoRow label="Tracking #" value={
                                    <Typography variant="body2" color="text.disabled" fontStyle="italic">Awaiting tracking number</Typography>
                                } />;
                            }
                            const carrier = getCarrierInfo(order.tracking_number);
                            return (
                                <>
                                    {carrier?.name && (
                                        <InfoRow label="Carrier" value={
                                            <Typography variant="body2" fontWeight={700}>{carrier.name}</Typography>
                                        } />
                                    )}
                                    <InfoRow
                                        label="Tracking #"
                                        value={
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={700}
                                                    sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}
                                                >
                                                    {order.tracking_number}
                                                </Typography>
                                                <Button
                                                    component="a"
                                                    href={carrier?.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<ShipIcon fontSize="small" />}
                                                    sx={{ borderRadius: 2, textTransform: 'none', fontSize: '0.75rem' }}
                                                >
                                                    Track Package
                                                </Button>
                                            </Box>
                                        }
                                    />
                                </>
                            );
                        })()}
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
};

export default PurchaseDetailPage;
